import { useState } from 'react';
import type { TcoPost, VlootTco } from '@fleet/shared';
import { BUCKETS, TCO_POSTEN, euro } from '@fleet/shared';
import { StackedBars, type Staaf } from '../../components/charts/StackedBars';
import { Dropdown } from '../../components/ui/Dropdown';
import { Eyebrow } from '../../components/ui/Eyebrow';
import { InfoTip } from '../../components/ui/InfoTip';
import { Panel } from '../../components/ui/Panel';

/** `a, b en c` — a Dutch list. */
function opsomming(delen: readonly string[]): string {
  if (delen.length <= 1) return delen[0] ?? '';
  return `${delen.slice(0, -1).join(', ')} en ${delen[delen.length - 1]}`;
}

/**
 * What the total is made of, in the two halves the chart stacks it in.
 *
 * Derived from `TCO_POSTEN` and `BUCKETS` rather than written out, so a new kind
 * of fixed cost or a new category shows up here the moment it exists instead of
 * quietly making this sentence wrong. `vastOverig` stays out: it is the catch-all
 * for a soort nobody has mapped, not a cost you would go looking for.
 */
const WAARUIT = `Vaste lasten (${opsomming(
  TCO_POSTEN.filter((p) => p.vast && p.key !== 'vastOverig').map((p) => p.label.toLowerCase()),
)}) plus variabele kosten (${opsomming(BUCKETS.map((b) => b.label.toLowerCase()))}).`;

/**
 * What the fleet costs, month by month, fixed and variable stacked.
 *
 * A separate panel from `MonthlyCostStack` rather than extra layers inside it.
 * Eleven layers in one stack flattens the small ones to nothing: depreciation
 * runs to some € 8.500 a month while washing is € 1.200 over a whole year, so
 * washing would be two pixels. They share the axis so they can be read against
 * each other — this one first, because the variable chart below is one of the two
 * halves this total is made of, and a half reads better once you have seen the
 * whole.
 *
 * The per-car split lives in `DuursteAutos`: same numbers, but the question
 * "which cars carry this" is not the question this chart answers.
 */
export function FleetTcoPanel({ tco }: { tco: VlootTco }) {
  const [keuze, setKeuze] = useState('');
  const reeks = tco.reeksen.find((r) => r.key === keuze) ?? tco.reeksen[0];
  if (!reeks) return null;

  // Totaal en lagen komen uit de gekozen reeks, niet uit een vast jaar: anders
  // staat er 2026 in de kop terwijl de staven 2025 tonen.
  const perPost = TCO_POSTEN.reduce(
    (acc, p) => {
      acc[p.key] = reeks.periodes.reduce((s, x) => s + x.posten[p.key], 0);
      return acc;
    },
    {} as Record<TcoPost, number>,
  );
  const totaal = TCO_POSTEN.reduce((s, p) => s + perPost[p.key], 0);
  const lagen = TCO_POSTEN.filter((p) => perPost[p.key] > 0);
  const staven: Staaf[] = reeks.periodes.map((p) => ({
    key: p.key,
    label: p.label,
    waarden: p.posten,
    gedempt: p.future,
  }));

  return (
    <Panel>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
          marginBottom: 16,
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Eyebrow>Total cost of ownership {reeks.label}</Eyebrow>
            <InfoTip uitlijning="links" tekst={WAARUIT} />
          </div>
          <div style={{ fontSize: 26, fontWeight: 900, color: '#2A6085', marginTop: 4 }}>
            {euro(totaal)}
          </div>
        </div>
        {tco.reeksen.length > 1 && (
          // Een dropdown en geen knoppenrij: bij vijf jaar data staan er anders
          // zes knoppen naast elkaar. Dezelfde keuze als op de autopagina.
          <Dropdown
            value={reeks.key}
            onChange={setKeuze}
            size="sm"
            minWidth={140}
            ariaLabel="Kies een periode"
            options={tco.reeksen.map((r) => ({ value: r.key, label: r.label }))}
          />
        )}
      </div>

      <StackedBars
        lagen={lagen}
        staven={staven}
        hoogte={210}
        totaalLabel={`${reeks.label} · totaal`}
      />
    </Panel>
  );
}
